import { useCallback, useMemo, useState } from 'react'
import type { MapAppearance } from '../../lib/mapAppearance'
import {
  defaultPrintPinSizes,
  printPinSizeRange,
  type PrintPinSizes,
} from '../../lib/mapPrintPayload'

/**
 * The print panel's pin and label sliders.
 *
 * Each slider FOLLOWS its default — the Pin size setting, the pin-names switch —
 * until the user moves it, and holds the user's value from then on. Seeding a
 * plain state once would freeze the default at whatever the setting was when
 * the panel mounted, so a Pin size changed afterwards would silently not reach
 * the sheet.
 */
export function usePrintPinSizes(appearance: MapAppearance): {
  sizes: PrintPinSizes
  pinRange: { min: number; max: number }
  onSizesChange: (next: PrintPinSizes) => void
} {
  const defaults = defaultPrintPinSizes(appearance)
  const [pinOverride, setPinOverride] = useState<number | null>(null)
  const [labelOverride, setLabelOverride] = useState<number | null>(null)

  const pinMm = pinOverride ?? defaults.pinMm
  const labelMm = labelOverride ?? defaults.labelMm
  // Memoised on the two numbers so a request builder depending on `sizes` is
  // not rebuilt on every render.
  const sizes = useMemo(() => ({ pinMm, labelMm }), [pinMm, labelMm])

  const onSizesChange = useCallback(
    (next: PrintPinSizes) => {
      if (next.pinMm !== pinMm) setPinOverride(next.pinMm)
      if (next.labelMm !== labelMm) setLabelOverride(next.labelMm)
    },
    [pinMm, labelMm],
  )

  return { sizes, pinRange: printPinSizeRange(defaults.pinMm), onSizesChange }
}
